<template>
  <div class="app-promo">
    <div class="app-promo__art" aria-hidden="true">
      <svg viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="48" cy="48" r="46" fill="var(--color-pink)" />
        <rect x="30" y="16" width="36" height="64" rx="8" fill="var(--color-white)" stroke="#B20FC0" stroke-width="2" />
        <rect x="34" y="24" width="28" height="42" rx="3" fill="var(--color-pink)" />
        <circle cx="48" cy="72" r="2.5" fill="#B20FC0" />
        <ellipse cx="48" cy="48" rx="10" ry="8" fill="var(--color-white)" stroke="#B20FC0" stroke-width="1.6" />
        <path d="M40 42l3-7 4 5M56 42l-3-7-4 5" stroke="#B20FC0" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="44.5" cy="47" r="1.1" fill="#B20FC0" />
        <circle cx="51.5" cy="47" r="1.1" fill="#B20FC0" />
        <path d="M46.5 51c1 .8 2 .8 3 0" stroke="#B20FC0" stroke-width="1.3" stroke-linecap="round" />
        <circle cx="22" cy="28" r="2" fill="#F5D0F8" />
        <circle cx="74" cy="34" r="3" fill="#F5D0F8" />
        <circle cx="70" cy="66" r="1.8" fill="#E8B4EF" />
      </svg>
    </div>
    <div class="app-promo__body">
      <strong>Возьмите Vetsy с собой</strong>
      <p>Веткарта, записи и мягкие напоминания о здоровье питомца — в приложении.</p>
      <div class="app-promo__actions">
        <a
            v-for="store in APP_STORE_BADGES"
            :key="store.id"
            class="app-promo__store"
            :href="store.url"
            target="_blank"
            rel="noopener"
            :aria-label="`Скачать из ${store.name}`"
            data-analytics="app_download_click"
            @click="trackAppDownload(store.id)"
        >
          <img :src="store.badge" :alt="`Скачать из ${store.name}`" height="36">
        </a>
      </div>
    </div>
    <div class="app-promo__qr" aria-label="QR-код приложения Vetsy">
      <div class="app-promo__qr-frame">
        <img src="/images/vetsy-app-qr.svg" width="108" height="108" alt="QR-код: скачать приложение Vetsy">
      </div>
      <span>Наведите камеру телефона</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {isLandingVisitMetrikaPath, reachMetrikaGoalWhenReady} from '~/utils/metrica-goals';
import {APP_STORE_BADGES, type AppStoreId} from '~/utils/appDownload';

const {public: {yandexMetrikaId}} = useRuntimeConfig();
const route = useRoute();

const trackAppDownload = (store: AppStoreId): void => {
  if (isLandingVisitMetrikaPath(route.path)) {
    reachMetrikaGoalWhenReady(yandexMetrikaId, 'app_download_click', {store});
  }
};
</script>

<style scoped lang="scss">
.app-promo {
  margin: 48px auto 0;
  max-width: 560px;
  text-align: left;
  background: linear-gradient(160deg, tint($color-pink, $color-white, 50%) 0%, $color-dark-white 55%, $color-white 100%);
  border: 1px solid tint($color-purple, $color-white, 13%);
  border-radius: 28px;
  padding: 22px 22px 22px 20px;
  display: grid;
  grid-template-columns: 112px 1fr auto;
  gap: 16px 18px;
  align-items: center;

  @include media(sm) {
    grid-template-columns: 1fr;
    padding: 20px 16px;
    gap: 12px;
    text-align: center;
  }

  &__art {
    width: 112px;
    height: 112px;
    border-radius: 24px;
    background: alpha($color-purple, 0.06);
    display: grid;
    place-items: center;
    overflow: hidden;

    @include media(sm) {
      width: 96px;
      height: 96px;
      margin: 0 auto;
    }

    svg {
      width: 96px;
      height: 96px;
      display: block;

      @include media(sm) {
        width: 84px;
        height: 84px;
      }
    }
  }

  &__body {
    min-width: 0;

    strong {
      display: block;
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 6px;
      letter-spacing: -0.02em;
      color: $color-black;
    }

    p {
      font-size: 14px;
      color: $color-gray;
      line-height: 1.45;
      margin: 0;
      text-align: left;

      @include media(sm) {
        text-align: center;
        margin: 0 0 14px;
      }
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;

    @include media(sm) {
      justify-content: center;
      margin-top: 0;
    }

    img {
      height: 36px;
      width: auto;
      display: block;
    }
  }

  &__qr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;

    @include media(sm) {
      display: none;
    }

    &-frame {
      background: #fff;
      padding: 12px;
      border-radius: 18px;
      border: 1px solid tint($color-purple, $color-white, 16%);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.8) inset,
        0 10px 28px alpha($color-purple, 0.1),
        0 2px 6px rgba(33, 39, 42, 0.04);

      img {
        width: 108px;
        height: 108px;
        display: block;
      }
    }

    span {
      font-size: 11px;
      font-weight: 500;
      color: $color-gray;
      line-height: 1.35;
      text-align: center;
      letter-spacing: 0.01em;
      max-width: 132px;
    }
  }
}
</style>
